import React from 'react';
import { useI18n } from './I18nContext';
import { translateText, translateWithEmojiPrefix } from './dictionary';

/**
 * <T> — drop-in text translator.
 *
 * Usage:
 *   <T>Home</T>                  → "Home" / "Inicio" / "Accueil" / ...
 *   <T emoji>💍 Proposal</T>     → "💍 Pedida de Mano" (preserves emoji)
 *   <T as="span">Book Now</T>    → <span>Book Now</span> wrapper element
 *
 * Why this pattern (instead of `t('hero.title')` keys):
 *   • The English source IS the key — no key naming to maintain.
 *   • Components read more naturally: <h1><T>Welcome</T></h1>
 *   • Dictionary lookups in src/i18n/dictionary.js use the English text
 *     directly, so updating English copy is a one-place change.
 *   • Falls through to original English when no translation exists, so
 *     forgetting to add a key never produces a broken UI.
 *
 * Performance: useI18n() returns a stable lang string; translateText() is
 * a synchronous object lookup. Cost per render is negligible.
 */
const T = ({ children, emoji = false, as: Component }) => {
  const { lang } = useI18n();
  const text = typeof children === 'string' ? children : String(children ?? '');
  const translated = emoji
    ? translateWithEmojiPrefix(text, lang)
    : translateText(text, lang);
  if (Component) {
    return <Component>{translated}</Component>;
  }
  return <>{translated}</>;
};

export default T;

/**
 * Hook variant — when you need the translated string outside JSX
 * (e.g. for aria-label, title attribute, alt text).
 *
 *   const t = useT();
 *   <button aria-label={t('Close')}>×</button>
 */
export function useT() {
  const { lang } = useI18n();
  return (englishText, opts) => {
    if (opts && opts.emoji) return translateWithEmojiPrefix(englishText, lang);
    return translateText(englishText, lang);
  };
}
